import { Button } from "@/components/ui/button";
import { type Income } from "@/hooks/admin/shared/useIncome";
import { useIncomeByBookingId } from "@/hooks/admin/shared/useIncomeByBookingId";
import { formatCurrency } from "@/utils/shared/currencyUtils";
import { formatFullNumericDate } from "@/utils/shared/dateUtils";
import { Edit, Trash2 } from "lucide-react";

interface IncomeTableProps {
  bookingId: string;
  onEditIncome?: (income: any) => void;
  onDeleteIncome?: (incomeId: string) => void;
}

const IncomeTable = ({
  bookingId,
  onEditIncome,
  onDeleteIncome,
}: IncomeTableProps) => {
  const { data: bookingIncomes = [], isLoading } = useIncomeByBookingId(bookingId);

  if (isLoading) {
    return <div className="text-sm text-gray-500">Cargando ingresos...</div>;
  }

  if (bookingIncomes.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No hay ingresos registrados para esta reservación
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 font-medium">Fecha</th>
            <th className="text-left py-2 font-medium">Pago Bruto</th>
            <th className="text-left py-2 font-medium">Pago Recibido</th>
            <th className="text-left py-2 font-medium">Tipo de Pago</th>
            {(onEditIncome || onDeleteIncome) && (
              <th className="text-right py-2 font-medium">Acciones</th>
            )}
          </tr>
        </thead>
        <tbody>
          {bookingIncomes.map((income) => (
            <tr
              key={income.id}
              className="border-b border-gray-100 last:border-b-0"
            >
              <td className="py-2">
                {formatFullNumericDate(income.payout_date)}
              </td>
              <td className="py-2">{formatCurrency(income.gross_payment)}</td>
              <td className="py-2">{formatCurrency(income.payout)}</td>
              <td className="py-2">{income.payment_type}</td>
              {(onEditIncome || onDeleteIncome) && (
                <td className="py-2 text-right">
                  <div className="flex justify-end gap-1">
                    {onEditIncome && (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => onEditIncome(income)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    )}
                    {onDeleteIncome && (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => onDeleteIncome(income.id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default IncomeTable;
